import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton, Switch, Tooltip } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Fade from '@material-ui/core/Fade'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import ApiKeyModal from 'Components/modals/api-key-modal/api-key-modal.component'
import DeleteItemModal from 'Components/modals/delete-item-modal/delete-item-modal.component'
import { useAuth } from 'Contexts/AuthProvider'
import useTranslation from 'Hooks/useTranslation'
import DmsService from 'Services/DMS/dms-api.service'
import AlertToastr from 'utils/alert'

const RemoveIcon = () => <div className="removeIcon" />
const EditIcon = () => <div className="editIcon" />
const IcoPlusWhite = () => <div className="plusIcon" />

const POLL_INTERVAL_MS = 30000

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}))

const truncateKey = (key) => {
  if (!key) return ''
  if (key.length <= 24) return key
  return `${key.substring(0, 20)}...`
}

const formatDate = (value) => {
  if (!value) return null
  return moment.utc(value).local().format('DD.MM.YYYY')
}

const ApiKeysPage = () => {
  const translation = useTranslation()
  const classes = useStyles()
  const { currentUser, currentCompany } = useAuth()

  const [apiKeys, setApiKeys] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
  const [apiKeyData, setApiKeyData] = useState(null)

  const companyId = currentCompany ? currentCompany.id : currentUser?.companyId
  const canChangeRateLimits = useMemo(
    () => currentUser.canChangeApiKeyRateLimits(),
    [currentUser]
  )

  const requestConfig = useMemo(() => {
    if (currentUser.isSuperuser() && companyId) {
      return { headers: { CompanyId: companyId } }
    }
    return {}
  }, [companyId, currentUser])

  const fetchApiKeys = useCallback(async (silent = false) => {
    if (!companyId && currentUser.isSuperuser()) return
    try {
      if (!silent) setIsLoading(true)
      const response = await DmsService.getApiKeys(requestConfig)
      setApiKeys(Array.isArray(response?.data) ? response.data : [])
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message
      if (message) AlertToastr.showErrorAlert(message)
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [companyId, currentUser, requestConfig])

  useEffect(() => {
    fetchApiKeys()
  }, [fetchApiKeys])

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchApiKeys(true)
    }, POLL_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [fetchApiKeys])

  const openCreateModal = () => {
    setApiKeyData(null)
    setModalIsOpen(true)
  }

  const openEditModal = (apiKey) => {
    setApiKeyData(apiKey)
    setModalIsOpen(true)
  }

  const copyKey = (key) => {
    if (!key) return
    navigator.clipboard.writeText(key)
    AlertToastr.showAlert(translation.settingsApiKeysCopied)
  }

  const onToggleActive = async (apiKey, event) => {
    event.stopPropagation()
    const nextActive = !apiKey.isActive
    try {
      const response = await DmsService.updateApiKey(
        {
          id: apiKey.id,
          name: apiKey.name,
          expiresAt: apiKey.expiresAt || null,
          rateLimitPerMinute: apiKey.rateLimitPerMinute,
          rateLimitPerHour: apiKey.rateLimitPerHour,
          rateLimitPerDay: apiKey.rateLimitPerDay,
          isActive: nextActive,
        },
        requestConfig
      )
      if (response?.data?.isSuccessful !== false) {
        AlertToastr.showAlert(
          response?.data?.message || translation.settingsApiKeysUpdatedSuccess
        )
        fetchApiKeys()
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message
      if (message) AlertToastr.showErrorAlert(message)
    }
  }

  const onDeleteApiKey = async () => {
    if (!apiKeyData?.id) return
    try {
      const response = await DmsService.deleteApiKey(apiKeyData.id, requestConfig)
      if (response?.data?.isSuccessful !== false) {
        AlertToastr.showAlert(
          response?.data?.message || translation.settingsApiKeysDeletedSuccess
        )
      }
      setApiKeyData(null)
      fetchApiKeys()
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message
      if (message) AlertToastr.showErrorAlert(message)
    }
  }

  return (
    <main>
      <div className="pageBlock">
        <div className="pageBtnSection pt0">
          <div className="part">
            <Button
              className="addItem"
              variant="contained"
              color="primary"
              startIcon={<IcoPlusWhite />}
              onClick={openCreateModal}
              id="settingsNewApiKeyBtn"
            >
              {translation.settingsTablesNewApiKeyLabel}
            </Button>
          </div>
        </div>

        <div className="pageCard noPadding">
          <div className="tableHolder">
            <table>
              <thead>
                <tr className="headingRow">
                  <th>{translation.settingsApiKeysNameHeader}</th>
                  <th>{translation.settingsApiKeysKeyHeader}</th>
                  <th className="text-center">{translation.settingsApiKeysStatusHeader}</th>
                  <th>{translation.settingsApiKeysExpiresHeader}</th>
                  <th>{translation.settingsApiKeysRateLimitsHeader}</th>
                  <th>{translation.settingsApiKeysUsageHeader}</th>
                  <th>{translation.settingsApiKeysLastUsedHeader}</th>
                  <th>{translation.settingsApiKeysCreatedHeader}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((apiKey) => {
                  const expiresLabel = apiKey.expiresAt
                    ? formatDate(apiKey.expiresAt)
                    : translation.settingsApiKeysNeverExpires
                  const lastUsedLabel = apiKey.lastUsedAt
                    ? moment.utc(apiKey.lastUsedAt).local().fromNow()
                    : translation.settingsApiKeysNeverUsed

                  return (
                    <tr key={apiKey.id}>
                      <td onClick={() => openEditModal(apiKey)}>
                        <span className="userName">{apiKey.name}</span>
                      </td>
                      <td>
                        <Tooltip title={translation.settingsApiKeysCopyLabel}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyKey(apiKey.key)}
                            id={`settingsApiKeyCopyBtn-${apiKey.id}`}
                          >
                            {truncateKey(apiKey.key)}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="text-center switchCell">
                        <Switch
                          checked={!!apiKey.isActive}
                          color="primary"
                          onClick={(e) => onToggleActive(apiKey, e)}
                          id={`settingsApiKeyActiveSwitch-${apiKey.id}`}
                        />
                      </td>
                      <td onClick={() => openEditModal(apiKey)}>
                        {expiresLabel}
                      </td>
                      <td onClick={() => openEditModal(apiKey)}>
                        {`${apiKey.rateLimitPerMinute} min | ${apiKey.rateLimitPerHour} hr | ${apiKey.rateLimitPerDay} day`}
                      </td>
                      <td onClick={() => openEditModal(apiKey)}>
                        {`${apiKey.usagePerMinute || 0}/${apiKey.rateLimitPerMinute} min | ${apiKey.usagePerHour || 0}/${apiKey.rateLimitPerHour} hr | ${apiKey.usagePerDay || 0}/${apiKey.rateLimitPerDay} day`}
                      </td>
                      <td onClick={() => openEditModal(apiKey)}>{lastUsedLabel}</td>
                      <td onClick={() => openEditModal(apiKey)}>{formatDate(apiKey.createdAt)}</td>
                      <td className="actionTableCell">
                        <IconButton
                          size="small"
                          className="deleteTableItemBtn"
                          onClick={() => openEditModal(apiKey)}
                          id={`settingsApiKeyEditBtn-${apiKey.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          className="deleteTableItemBtn"
                          onClick={() => {
                            setApiKeyData(apiKey)
                            setDeleteModalIsOpen(true)
                          }}
                          id={`settingsApiKeyDeleteBtn-${apiKey.id}`}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className={classes.root}>
              <Fade in={isLoading && apiKeys.length === 0} unmountOnExit>
                <CircularProgress />
              </Fade>
            </div>
          </div>
        </div>
      </div>

      <ApiKeyModal
        isOpen={modalIsOpen}
        isOpenChangeHandler={setModalIsOpen}
        apiKeyData={apiKeyData}
        setApiKeyData={setApiKeyData}
        fetchApiKeys={fetchApiKeys}
        canChangeRateLimits={canChangeRateLimits}
        requestConfig={requestConfig}
      />
      <DeleteItemModal
        isOpen={deleteModalIsOpen}
        isOpenManager={setDeleteModalIsOpen}
        message={translation.settingsApiKeysDeleteConfirm}
        onDeleteModalClose={onDeleteApiKey}
      />
    </main>
  )
}

export default ApiKeysPage
